import { Component } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RefreshCw, Home } from 'lucide-react';

// Bundle velho em cache: o main.js publicado (asset-manifest) não é o que está rodando, ou seja, JS antigo
// contra a API nova. Usado no load (App.js) e depois de um crash (aqui). Erro de rede propaga ao chamador.
export async function bundleDesatualizado() {
  const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
  if (!res.ok) return false;
  const manifest = await res.json();
  const publicado = manifest?.files?.['main.js'] || '';
  const emExecucao = Array.from(document.scripts)
    .map((s) => s.src)
    .find((src) => src.includes('/static/js/main.'));
  return Boolean(publicado && emExecucao && !emExecucao.includes(publicado));
}

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, updating: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  // Auto-cura do bundle velho em cache: se o main.js em execução não é mais o
  // do asset-manifest publicado, o crash provavelmente é "JS antigo × API
  // nova". Revalida o HTML e recarrega UMA vez (guarda em sessionStorage
  // contra loop) — o cliente cai direto na versão nova, sem precisar saber
  // recarregar. Se a versão já é a atual, é erro real: mostra a tela de erro.
  // Crash causado por tradutor de página (Google Translate & cia): ele troca os
  // nós de texto por <font>, e o React estoura ao mexer neles. O DOM já está
  // corrompido — não adianta re-renderizar, o certo é recarregar. Guardado por
  // sessão para nunca virar loop.
  _isDomCorrompido(error) {
    const msg = String(error?.message || error);
    return /insertBefore|removeChild|The object can not be found here/i.test(msg);
  }

  _reloadPorTradutor() {
    try {
      if (sessionStorage.getItem('eb_dom_reload')) return false;
      sessionStorage.setItem('eb_dom_reload', '1');
      this.setState({ updating: true });
      window.location.reload();
      return true;
    } catch {
      return false;
    }
  }

  async _tryAutoHeal() {
    try {
      if (sessionStorage.getItem('eb_auto_reload')) return false;
      if (!(await bundleDesatualizado())) return false;

      sessionStorage.setItem('eb_auto_reload', '1');
      this.setState({ updating: true });
      // Força o HTML a revalidar no cache HTTP antes do reload (contorna o
      // cache heurístico dos deploys antigos, que ainda não tinham no-cache).
      try { await fetch(window.location.href, { cache: 'reload' }); } catch { /* segue */ }
      window.location.reload();
      return true;
    } catch {
      return false;
    }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo);
    // Telemetria: manda o erro pro backend (db.client_errors). O checkout roda
    // no celular do CLIENTE — sem isso, o stack morre num console que nunca
    // veremos. Fire-and-forget: telemetria jamais pode quebrar o boundary.
    try {
      const base = process.env.REACT_APP_BACKEND_URL || '';
      fetch(`${base}/api/client-error`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: String(error?.message || error).slice(0, 500),
          stack: String(error?.stack || '').slice(0, 4000),
          component_stack: String(errorInfo?.componentStack || '').slice(0, 4000),
          url: String(window.location.href).slice(0, 300),
          user_agent: String(navigator.userAgent).slice(0, 300),
        }),
      }).catch(() => {});
    } catch { /* nunca propaga */ }

    // DOM corrompido por tradutor tem prioridade: recarrega direto (o
    // asset-manifest do auto-heal não diria nada útil nesse caso).
    if (this._isDomCorrompido(error) && this._reloadPorTradutor()) return;

    this._tryAutoHeal();
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Auto-cura em andamento: versão nova detectada, recarregando — não mostra
    // a tela de erro (o cliente só vê um respiro de "Atualizando…").
    if (this.state.updating) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-gray-50 dark:bg-gray-950">
          <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
          <p className="text-sm text-gray-600 dark:text-gray-400">Atualizando o sistema…</p>
        </div>
      );
    }

    const isDev = process.env.NODE_ENV === 'development';

    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle className="text-xl">
              Algo deu errado
            </CardTitle>
            <CardDescription className="text-gray-600 dark:text-gray-400">
              Ocorreu um erro inesperado na aplicação. Você pode tentar recarregar a página ou voltar à tela inicial.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isDev && this.state.error && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-left">
                <p className="text-xs font-semibold text-red-800 dark:text-red-200 mb-2">
                  Detalhes (apenas em desenvolvimento):
                </p>
                <p className="text-xs font-mono text-red-700 dark:text-red-300 break-all">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo?.componentStack && (
                  <pre className="mt-2 max-h-40 overflow-auto text-[10px] font-mono text-red-600 dark:text-red-400 whitespace-pre-wrap">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 pt-2 sm:flex-row">
              <Button onClick={this.handleReset} variant="outline" className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Tentar novamente
              </Button>
              <Button onClick={this.handleReload} variant="outline" className="gap-2">
                <RefreshCw className="h-4 w-4" />
                Recarregar página
              </Button>
              <Button onClick={this.handleGoHome} className="gap-2">
                <Home className="h-4 w-4" />
                Ir para o início
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
}

export default ErrorBoundary;
